import { ColorSwatch, Theme } from './types';

/**
 * Generates a color swatch based on a given hue in OKLCH format.
 * The swatch contains 10 colors ranging from light to dark, with varying saturation.
 *
 * @param hue - The hue value in OKLCH format (0-360)
 * @param baseSaturation - Base saturation value (0-1)
 * @param id - Optional ID for the swatch
 * @returns A ColorSwatch object with 10 colors
 */
export function generateColorSwatch(
  hue: number,
  baseSaturation = 0.1,
  id: string = crypto.randomUUID(),
): ColorSwatch {
  const colors: string[] = [];

  // Generate 10 colors from light to dark
  for (let i = 0; i < 10; i++) {
    // Lightness decreases from 99.5% to 20%
    const lightness = 99.5 - i * 8.83;
    // Saturation increases slightly as we go darker
    const saturation = baseSaturation * (1 + i * 0.05);

    colors.push(`oklch(${lightness}% ${saturation} ${hue})`);
  }

  return {
    id,
    keyColor: colors[5], // Middle color as the key color
    colors,
  };
}

/**
 * Generates a complementary color scheme based on a primary hue.
 * Uses different algorithms to create varied and interesting color combinations.
 *
 * @param primaryHue - The primary hue value in OKLCH format (0-360)
 * @param variant - Whether the theme is light or dark
 * @returns A Theme object with coordinated color swatches
 */
export function generateTheme(primaryHue?: number): Theme {
  // If no primary hue provided, generate a random one
  const hue = primaryHue ?? Math.floor(Math.random() * 360);

  // Randomly select a color scheme algorithm
  const algorithm = Math.floor(Math.random() * 3);

  let secondaryHue: number;
  let accentHue: number;

  switch (algorithm) {
    case 0: // Analogous scheme
      secondaryHue = (hue + 30) % 360;
      accentHue = (hue + 60) % 360;
      break;
    case 1: // Split complementary
      secondaryHue = (hue + 150) % 360;
      accentHue = (hue + 210) % 360;
      break;
    case 2: // Triadic
      secondaryHue = (hue + 120) % 360;
      accentHue = (hue + 240) % 360;
      break;
    default:
      secondaryHue = (hue + 30) % 360;
      accentHue = (hue + 60) % 360;
  }

  // Generate swatches with varying saturation levels
  const neutral = generateColorSwatch(hue, 0.01);
  const primary = generateColorSwatch(hue, 0.1);
  const secondary = generateColorSwatch(secondaryHue, 0.05);
  const accent = generateColorSwatch(accentHue, 0.2);

  return {
    neutral,
    primary,
    accent,
    secondary,
  };
}

/**
 * Generates CSS variables for a theme in the format used by the landing page.
 *
 * @param theme - The theme to generate CSS variables for
 * @returns A string containing the CSS variables in :root block
 */
export function generateThemeCSS(swatches: Theme): string {
  return Object.entries(swatches)
    .flatMap(([prefix, swatch]) => {
      return swatch.colors.map((color, colorIndex) => `--c-${prefix}-${colorIndex}: ${color};`);
    })
    .join('\n');
}
